import { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { Star, ArrowLeft, Send } from "lucide-react";

interface ReviewFormProps {
	orderId: string;
	printerId: string;
	onBack: () => void;
	onSubmitted: () => void;
}

function StarRating({
	value,
	onChange,
	label,
}: {
	value: number;
	onChange: (v: number) => void;
	label: string;
}) {
	const [hover, setHover] = useState(0);
	return (
		<div>
			<Label className="text-sm font-medium mb-1.5 block">{label}</Label>
			<div className="flex gap-1">
				{[1, 2, 3, 4, 5].map((s) => (
					<button
						key={s}
						type="button"
						onClick={() => onChange(s)}
						onMouseEnter={() => setHover(s)}
						onMouseLeave={() => setHover(0)}
						className="p-0.5 transition-transform hover:scale-110"
					>
						<Star
							className={`w-6 h-6 transition-colors ${
								s <= (hover || value)
									? "fill-amber-400 text-amber-400"
									: "text-muted-foreground/30"
							}`}
						/>
					</button>
				))}
			</div>
		</div>
	);
}

export function ReviewForm({ orderId, printerId, onBack, onSubmitted }: ReviewFormProps) {
	const [rating, setRating] = useState(0);
	const [qualityRating, setQualityRating] = useState(0);
	const [speedRating, setSpeedRating] = useState(0);
	const [communicationRating, setCommunicationRating] = useState(0);
	const [comment, setComment] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = async () => {
		if (rating === 0 || !comment.trim()) return;
		setSubmitting(true);
		try {
			await fetch("/api/reviews", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					printerId,
					orderId,
					authorName: "You",
					authorAvatar: "",
					rating,
					qualityRating: qualityRating || rating,
					speedRating: speedRating || rating,
					communicationRating: communicationRating || rating,
					comment,
					photos: [],
				}),
			});
			onSubmitted();
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="max-w-2xl mx-auto space-y-6">
			<Button
				variant="ghost"
				size="sm"
				onClick={onBack}
				className="gap-2 text-muted-foreground hover:text-foreground"
			>
				<ArrowLeft className="w-4 h-4" />
				Back to Orders
			</Button>

			<Card>
				<CardContent className="p-6">
					<h2 className="text-xl font-bold mb-6">Write a Review</h2>

					<div className="space-y-5">
						<StarRating value={rating} onChange={setRating} label="Overall Rating" />

						<Separator />

						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
							<StarRating value={qualityRating} onChange={setQualityRating} label="Quality" />
							<StarRating value={speedRating} onChange={setSpeedRating} label="Speed" />
							<StarRating
								value={communicationRating}
								onChange={setCommunicationRating}
								label="Communication"
							/>
						</div>

						<Separator />

						<div>
							<Label className="text-sm font-medium mb-2 block">Your Review</Label>
							<Textarea
								value={comment}
								onChange={(e) => setComment(e.target.value)}
								placeholder="Share your experience with this print service..."
								rows={4}
							/>
						</div>

						<Button
							onClick={handleSubmit}
							disabled={rating === 0 || !comment.trim() || submitting}
							className="w-full gap-2"
							size="lg"
						>
							<Send className="w-4 h-4" />
							{submitting ? "Submitting..." : "Submit Review"}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
